import { history } from "settings";
import { store } from "store";
import { ActionType } from "store/auth/types";
import { storage, API_BASE_URL } from "utils";
import { INITIAL_DEV_SURFACE, mintDevSession } from "utils/devSurface";
// Bare src path, not the `utils` barrel — the barrel is stubbed in the guard suite,
// and this module must load for real there (see `support/platform-module-loader.mjs`).
import { recordApiFailure } from "utils/errorContext";
import { ROUTES } from "routes";
// A src-relative BARE path, not "./platformSession": the guard suite's module loader resolves
// relative imports of this file to an empty module, but loads bare src paths for real.
import { SESSION_ENDED_MESSAGE, isAccessTokenExpired, renewPlatformSession } from "services/platformSession";

import axios, { type AxiosRequestConfig, type AxiosResponse } from "axios";

export const instance = axios.create({
  baseURL: API_BASE_URL,
});

const _authorizing: Promise<void> | null = null;

// Dev-only: the dev session bypass has no refresh token, so without this any 401
// would immediately bounce to /sign-in. When DEV_SESSION is on we re-mint the dev
// token once and retry the request instead. No-op in production (flag is false).
const DEV_SESSION = import.meta.env.VITE_DEV_SESSION === "true";
const DEV_SESSION_SECRET = import.meta.env.VITE_DEV_SESSION_SECRET as
  | string
  | undefined;

// Surface for dev-session re-mints, captured ONCE at module load from the URL you loaded —
// the SAME stable basis useWorkOSAuth uses (§17). Deriving it live in the 401-retry was wrong:
// the InternalRoute guard bounces /internal/* → /certificates before the retry fires, so the
// live pathname reads "company" and the retry minted a company_admin token that clobbered the
// internal token from checkAuth (last-writer-wins race).
//
// VRG-564: this used to be a hand-rolled COPY of useWorkOSAuth's derivation, carrying the
// comment "Must match useWorkOSAuth's DEV_DEFAULT_SURFACE" — one rule, two copies kept in
// step by hand (§36). Both now read the same constant, so they cannot disagree.
const DEV_INITIAL_SURFACE = INITIAL_DEV_SURFACE;

const retryWithToken = (config: any) => {
  const token = storage.getItem("accessToken");
  if (config?.headers) {
    delete config.headers["X-Vera-Token"];
    if (token) config.headers["X-Vera-Token"] = `${token}`;
  }
  return axios.request(config);
};

// Re-mint the dev session token once per request (guarded so a genuinely-401
// endpoint can't loop). Returns true if a fresh token was set.
const reestablishDevSessionOnce = async (config: any): Promise<boolean> => {
  if (!DEV_SESSION || !DEV_SESSION_SECRET) return false;
  if (config?.__devReestablished) return false;
  try {
    // Load-time surface so this re-mint agrees with the page's account (§17) and never
    // clobbers an internal token with a company one (see DEV_INITIAL_SURFACE). Shared
    // mint (VRG-564) so a re-mint racing checkAuth's initial mint joins it rather than
    // issuing a second POST.
    const token = await mintDevSession({
      apiBaseUrl: API_BASE_URL,
      secret: DEV_SESSION_SECRET,
      surface: DEV_INITIAL_SURFACE,
    });
    if (token) {
      storage.setItem("accessToken", token);
      if (config) config.__devReestablished = true;
      return true;
    }
  } catch {
    /* fall through to logout */
  }
  return false;
};

const logOut = async () => {
  storage.removeItem("accessToken");
  storage.removeItem("refreshToken");
  storage.removeItem("selectedCompany");

  store.dispatch({
    type: ActionType.SET_AUTH,
    payload: null,
  });

  history.push("/sign-in");
};

/**
 * Reject, rather than resolve, on a failed request.
 *
 * This function exists because `errorHandler` used to `return error.response` for
 * every non-401 status. Returning it means axios RESOLVES: `createApiCall` hands the
 * error response back as an ordinary value, react-query's `mutationFn` returns
 * normally, `onSuccess` fires, and the call site shows a success toast for a request
 * that failed. A 500 was indistinguishable from a 200.
 *
 * The workaround had spread rather than the cause being fixed: ~19 hooks and several
 * sagas/services hand-rolled `if (status >= 400) throw`, and three files carry comments
 * explaining the trap (`usePackageProduct`, `useUpdatePackage`, `announcementsApi`).
 * Those checks now sit behind this throw and are harmless dead code; the ~45 call sites
 * that never checked are the ones this fixes.
 *
 * The rejection is the ORIGINAL axios error so existing consumers keep working:
 *   - `getErrorMessage` reads `error.response.data.message` first, so toasts still show
 *     the backend's reason rather than "Request failed with status code 500".
 *   - `error.response.status` keeps working for axios-shaped consumers.
 *   - `.status` is also attached flat, because `usePackageProduct` types its rejection
 *     as `Error & { status?: number }` and its callers branch on a 409 that way.
 */
const rejectWithResponse = (error: any) => {
  if (error && error.response && typeof error.status !== "number") {
    error.status = error.response.status;
  }
  // VRG-721 — the API now returns the `ref` its exception filter logged this failure
  // under. Kept FLAT on the error, beside `.status`, so anything holding the rejection
  // can quote it without knowing the response shape.
  const ref = error?.response?.data?.ref;
  if (error && typeof ref === "string" && typeof error.ref !== "string") {
    error.ref = ref;
  }
  // VRG-721 — and remembered, briefly, so the error toast this rejection is about to
  // produce can carry the failure into "Report a problem" without every one of ~160
  // toast call sites having to pass the error through. See `utils/errorContext`.
  recordApiFailure(error);
  throw error;
};

// @ts-ignore
const errorHandler = async (error: any) => {
  // No response at all — DNS failure, connection refused, CORS, timeout. This used to
  // resolve with a plain-object copy of the error, which made a dead API look to every
  // caller exactly like a successful empty response.
  if (!error.response) {
    return rejectWithResponse(error);
  }

  if (error.response.status === 401) {
    // Platform session renewal: the access token lives ~15 minutes, so a 401 usually just
    // means it expired. Renew once from the HttpOnly session cookie and retry the request.
    // `__sessionRenewed` stops a request that 401s AGAIN after renewal (a real permission
    // or revocation problem) from looping. The renewal call itself uses raw axios, not
    // createApiCall, so it can never land back in this handler.
    if (!error.config?.__sessionRenewed && !DEV_SESSION) {
      if (error.config) error.config.__sessionRenewed = true;
      const staleToken = error.config?.headers?.["X-Vera-Token"] ?? null;
      const outcome = await renewPlatformSession(staleToken);
      if (outcome.status === "renewed") {
        return retryWithToken(error.config);
      }
      if (outcome.status === "unavailable") {
        // The API could not answer (5xx / network). Never a sign-out: keep the session and
        // let the caller surface the error; the next request will try again.
        return rejectWithResponse(error);
      }
      // Ended. If the access token is ALSO spent, the session is over (revoked, idle 7 days,
      // past the 30-day cap, or a pre-registry sign-in with no session cookie): a full
      // sign-in is needed, which challenges the second factor again. If the token is still
      // within its lifetime, this 401 was the endpoint's own answer (e.g. a wrong current
      // password) and must not sign anyone out — unless the guard said the session itself
      // was signed out.
      const message = String(error.response?.data?.message ?? "");
      if (isAccessTokenExpired() || message.includes(SESSION_ENDED_MESSAGE)) logOut();
      return rejectWithResponse(error);
    } else if (await reestablishDevSessionOnce(error.config)) {
      // Dev session self-heal — re-minted token, retry instead of /sign-in.
      return retryWithToken(error.config);
    } else {
      // VRG-564 removed a THIRD mint site that lived here: a second hand-rolled copy of
      // `reestablishDevSessionOnce` — same POST, same surface, but guarded on its OWN flag
      // (`_devRetried`) rather than the one the function it duplicated sets
      // (`__devReestablished`). Being in the `else` of that very call, it could only run in
      // three states, none of them useful:
      //
      //   - DEV_SESSION off / no secret        → its own `devEnabled && devSecret` was false
      //   - the mint had just FAILED           → an immediate blind re-POST of a request
      //                                          that failed microseconds earlier
      //   - the mint had already SUCCEEDED for this request and the retry STILL 401'd
      //                                        → the separate flag let it mint a second
      //                                          time, but a fresh token that is still
      //                                          rejected is a permission problem, not a
      //                                          stale one. Two attempts only doubled the
      //                                          latency before the real error surfaced.
      //
      // So the behavioural delta is one re-mint per request instead of two, and one fewer
      // of the three POSTs a cold load fired.
      //
      // Let the calling hook handle a genuine 401 — as a REJECTION, so an expired
      // session surfaces as an error instead of an empty successful response.
      return rejectWithResponse(error);
    }
  }

  // VRG-361: the demo console trial expired (AssertConsoleTrialActiveUseCase on the API).
  // NOT a permission gap — every company-scoped endpoint now answers this the same way,
  // so redirect to the one page that still works, rather than letting the calling
  // component render its own "insufficient permissions" message for what is really an
  // account-wide gate. Mirrors apps/app's existing COMPANY_SUSPENDED interceptor
  // (services/workos-auth.service.ts) — same shape, different code, different page.
  if (
    error.response.status === 403 &&
    error.response.data?.code === "CONSOLE_TRIAL_EXPIRED" &&
    typeof window !== "undefined" &&
    !window.location.pathname.startsWith(ROUTES.TRIAL_ENDED)
  ) {
    history.push(ROUTES.TRIAL_ENDED);
    // Redirect AND reject. Returning the response here used to be the last resolve-on-error
    // left in the client, and it made the contract conditional on the current URL: the same
    // 403 rejected once you were already on TRIAL_ENDED (the guard above is a `!startsWith`)
    // and resolved everywhere else. A caller could therefore receive an error body typed as
    // its success shape purely because of which page it was on. The redirect is what handles
    // the navigation; the rejection is what stops the caller reading `data.code` as data.
    return rejectWithResponse(error);
  }

  // Don't logout on 403 - user is authenticated but lacks permission for this specific resource
  // The application should handle these gracefully with error messages — which now means
  // catching a rejection (or letting react-query's onError fire), not inspecting a
  // resolved response's status.
  return rejectWithResponse(error);
};

/**
 * THE CONTRACT (VRG-386). `createApiCall` REJECTS on every failed request.
 *
 * There is exactly one rule and no exceptions to it:
 *
 *   status <  400            -> resolves with the AxiosResponse
 *   status >= 400            -> REJECTS with the original AxiosError
 *   no response at all       -> REJECTS (network down, CORS, timeout, connection refused)
 *
 * The 401 and CONSOLE_TRIAL_EXPIRED paths still run their side effects first — a 401 refreshes
 * the token and RETRIES (a successful retry resolves normally, so a refresh is invisible to the
 * caller), and a trial-expired 403 redirects. But once those side effects are done, a request
 * that ultimately failed rejects like any other. No status resolves as a value.
 *
 * Why this is stated as an absolute: the bug this replaced was not that errors resolved, it was
 * that SOME of them did. `errorHandler` returned `error.response` from a `.catch`, which RESOLVES
 * the promise, so react-query's `onSuccess` fired on a 500 and the call site showed a success
 * toast for a write that never happened (observed: `PATCH /demos/admin/:companyId/expiry` 500'd
 * and the UI said "Demo lifetime updated"). Because that was inconsistent rather than uniform,
 * every call site had to know which statuses were values and which were errors, and ~19 of them
 * hand-rolled `if (status >= 400) throw` to compensate while ~45 did not and silently reported
 * success. A conditional contract is what made the failure invisible, so the fix is a total one.
 *
 * Consequences for callers:
 *   - react-query: `onError` fires, `isError` is true. Do NOT check `response.status` for failure.
 *   - An EXPECTED non-2xx (a 404 that means "none yet", a 409 conflict) is expressed by CATCHING
 *     and inspecting `error.status`, never by reading a resolved response.
 *   - `error.status` is attached flat alongside `error.response.status`, and `getErrorMessage`
 *     reads `error.response.data.message`, so toasts keep showing the backend's reason.
 *
 * Guarded by `apps/platform/test/api-client-errors.guard.spec.ts`.
 */
// @ts-ignore
export const createApiCall = ({
  method = "GET",
  url = "",
  data = {},
  headers = {},
  ...rest
}: AxiosRequestConfig): Promise<AxiosResponse> => {
  const isFormData =
    typeof FormData !== "undefined" && data instanceof FormData;

  const reqHeaders: any = {
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    Authorization: null,
    cache: "no-cache",
    ...headers,
  };

  const token = storage.getItem("accessToken");
  const company = storage.getItem("selectedCompany");

  if (token) {
    reqHeaders["X-Vera-Token"] = `${token}`;
  }

  if (company) {
    reqHeaders["X-Vera-Company"] = `${company}`;
  }

  // VERA-876: when Vera staff are operating inside a company's space, inject the
  // X-Vera-Space header so the API can cap their effective permissions to that
  // company's vera_support grant. The header value is the company UUID.
  // The space state is persisted by zustand under the 'veraSpace' localStorage key.
  try {
    const rawSpace = localStorage.getItem("veraSpace");
    if (rawSpace) {
      const parsed = JSON.parse(rawSpace) as { state?: { companyId?: string } };
      const spaceCompanyId = parsed?.state?.companyId;
      if (spaceCompanyId) {
        reqHeaders["X-Vera-Space"] = spaceCompanyId;
        // The DATA scope (X-Vera-Company) MUST equal the space company — the guard
        // rejects a mismatch (a staffer can't borrow one brand's grant to read
        // another's data). Derive it from the SAME store rather than the fragile
        // `selectedCompany` key (which the auth/profile flow clears for platform
        // admins), so the two headers always agree while in space.
        reqHeaders["X-Vera-Company"] = spaceCompanyId;
      }
    }
  } catch {
    // localStorage unavailable or parse failed — skip silently
  }

  return instance
    .request({ method, url, data, headers: reqHeaders, ...rest })
    .then((response) => response)
    .catch(errorHandler);
};

// Export api for use in hooks
export const api = {
  get: (url: string, config?: AxiosRequestConfig) =>
    createApiCall({ method: "GET", url, ...config }),
  post: (url: string, data?: any, config?: AxiosRequestConfig) =>
    createApiCall({ method: "POST", url, data, ...config }),
  put: (url: string, data?: any, config?: AxiosRequestConfig) =>
    createApiCall({ method: "PUT", url, data, ...config }),
  delete: (url: string, config?: AxiosRequestConfig) =>
    createApiCall({ method: "DELETE", url, ...config }),
  patch: (url: string, data?: any, config?: AxiosRequestConfig) =>
    createApiCall({ method: "PATCH", url, data, ...config }),
};

/**
 * Read the HTTP status off a rejection from `createApiCall`.
 *
 * Prefer this over reaching into `error.response.status` by hand: the client attaches the
 * status flat as well, and the two have differed historically (a hand-thrown `{ response }`
 * object has no flat `.status`, a hand-thrown `Error` has no `.response`). Returns undefined
 * for a rejection that never reached the server — a network failure has no status, and
 * treating that as 0 or 500 is how a transport error gets reported as a server error.
 */
export const statusOf = (error: any): number | undefined => {
  const status = error?.status ?? error?.response?.status;
  return typeof status === "number" ? status : undefined;
};

/**
 * "Authenticated but not allowed" — what an access-denied surface renders instead of
 * "couldn't load, retry", which would invite a retry that can never succeed.
 *
 * Defined ONCE here rather than per-API. `announcementsApi` and `supportApi` each grew a
 * byte-identical copy, and because this file re-exports both with `export *`, the name
 * became AMBIGUOUS — ES modules exclude an ambiguous star-export from the namespace
 * entirely, so `import { isForbidden } from 'services'` evaluated to `undefined` and
 * silently re-broke the access-denied UI the VRG-386 sweep had just restored. Typecheck
 * caught it as TS2308; at runtime it would have failed quietly.
 *
 * Lives beside `statusOf` because that is the concern: reading a status off either error
 * shape.
 */
export const isForbidden = (error: unknown): boolean => statusOf(error) === 403;

/**
 * Express "this non-2xx is an expected outcome, not a failure".
 *
 * Since `createApiCall` rejects on every >= 400, a status that is a normal part of the
 * domain — a 404 meaning "none configured yet", a 409 meaning "already exists" — has to be
 * caught rather than read off a resolved response. This is the ONE supported way to do that,
 * so the intent stays greppable and a reader can tell a deliberate empty state apart from a
 * swallowed error:
 *
 *   resolveOnStatus(api.get('/subscriptions/current').then(r => r.data), [404], null)
 *
 * Anything not in `codes` still rejects, so this narrows the contract rather than opting out
 * of it. Never pass a 5xx — a server error is not an expected outcome.
 */
export const resolveOnStatus = async <T, F>(
  request: Promise<T>,
  codes: number[],
  fallback: F
): Promise<T | F> => {
  try {
    return await request;
  } catch (error) {
    const status = statusOf(error);
    if (status !== undefined && codes.includes(status)) return fallback;
    throw error;
  }
};

export * from "./productApi";
export * from "./productImportUrlApi";
export * from "./productIntegrationApi";
export * from "./tagApi";
export * from "./shopifyApi";
export * from "./integrationRequestsApi";
export * from "./fieldMappingApi";
export * from "./workos-auth.service";
export * from "./adminApi";
export * from "./billingApi";
export * from "./customerApi";
export * from "./subscriptionApi";
export * from "./companyActivityApi";
export * from "./tagShopApi";
export * from "./certificateActivityApi";
export * from "./signupApi";
export * from "./scanResolveApi";
export * from "./supportApi";
export * from "./tourProgressApi";
export * from "./tourAnalyticsApi";
export * from "./askStatsApi";
export * from "./websiteAnalyticsApi";
export * from "./seoApi";
export * from "./competitorResearchApi";
export * from "./announcementsApi";
export * from "./mediaApi";
export * from "./marketItemsApi";
export * from "./marketSourcesApi";
export * from "./askClaimsApi";
export * from "./analyticsTopInfluencersApi";
export * from "./analyticsCountryMetricsApi";

export * from "./demoDataApi";
export * from "./introDataApi";
export * from "./gtmApi";
export * from "./verafiedApi";
export * from "./companyWebsiteDisplayApi";
